import React from 'react'

import {
  Table,
  Thead,
  Tr,
  Tbody,
  TableColumnHeaderProps,
  TableCellProps,
  Th,
  LinkBox,
  Td,
  LinkOverlay,
  TableRowProps,
} from '@chakra-ui/react'
import { Link } from 'react-router'

export type LinkTableColumn<Key extends string> = {
  key: Key
  renderHeader: React.ReactNode
  isVisible?: boolean
  thProps?: TableColumnHeaderProps
}
type LinkTableRow<Key extends string> = {
  rowId: string
  trProps?: TableRowProps
  columnValues: Record<
    Key,
    {
      renderColumn: React.ReactNode
      tdProps?: TableCellProps
    }
  >
}

type Props<Data extends unknown[], Key extends string> = {
  data: Data
  columns: LinkTableColumn<Key>[]
  linkTarget: Key
  renderRow: (data: Data[number]) => LinkTableRow<Key>
  link: (data: Data[number]) => string
}

/**
 * `LinkTable` は、一覧ページなどで使用する、各行がリンクとして機能するテーブルコンポーネントです。
 *
 * ChakraUIの `LinkOverlay` を利用して実装しています。
 *
 * @example
 *
 * ```tsx
 * const users = [
 *   { id: '1', name: '鈴木太郎: 'taro@example.com' },
 *   { id: '2', name: '山田花子: 'hanako@example.com' },
 * ];
 *
 *
 * const App = () => (
 *   <LinkTable
 *     data={users}
 *     columns={[
 *       { key: 'name', renderHeader: 'Name' },
 *       { key: 'email', renderHeader: 'Email' },
 *     ]}
 *     linkTarget="name" // リンクの文字列として扱うカラムを指定
 *     renderRow={user => ({
 *       rowId: user.id,
 *       columnValues: {
 *         name: { renderColumn: user.name },
 *         email: { renderColumn: user.email },
 *        },
 *     })}
 *     link={user => `/users/${user.id}`}
 *   />
 * );
 * ```
 */
export const LinkTable = <Data extends unknown[], Key extends string>({
  data,
  columns,
  linkTarget,
  renderRow,
  link,
}: Props<Data, Key>) => {
  return (
    <Table variant="hoverable">
      <Thead position="sticky" zIndex="1" top={0} bg="white">
        <Tr>
          {columns.map(column => {
            if (column.isVisible === false) {
              return null
            }
            const key = `th_${column.key}`
            return (
              <Th key={key} {...column.thProps}>
                {column.renderHeader}
              </Th>
            )
          })}
        </Tr>
      </Thead>
      <Tbody>
        {data.map(d => {
          const rowData = renderRow(d)
          return (
            <LinkBox
              as={Tr}
              key={rowData.rowId}
              transform="scale(1)"
              {...rowData.trProps}
            >
              {columns.map(column => {
                const cellValue = rowData.columnValues[column.key]
                if (column.isVisible === false) {
                  return null
                }
                const tdKey = `td_${rowData.rowId}_${column.key}`
                return column.key === linkTarget ? (
                  <Td key={tdKey} {...cellValue.tdProps}>
                    <LinkOverlay as={Link} to={link(d)} cursor="pointer">
                      {cellValue.renderColumn}
                    </LinkOverlay>
                  </Td>
                ) : (
                  <Td key={tdKey} {...cellValue.tdProps}>
                    {cellValue.renderColumn}
                  </Td>
                )
              })}
            </LinkBox>
          )
        })}
      </Tbody>
    </Table>
  )
}
